import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { endpointService } from "src-next/modules/crm-clinical/messaging/services/endpointService";

import type { INotificationData } from "./types";

/** Fallback do plano Fluxmed quando não há Endpoint Meta ativo. */
const DEFAULT_PHONE_NUMBER_ID =
  import.meta.env.VITE_FLUXZAP_DEFAULT_PHONE_NUMBER_ID?.trim() ||
  "379190331933594";

const COBRANCA_TEMPLATE_SUFFIX = "_cobranca_de_consulta";

const NOTIFICATION_CHANNEL = ["whatsApp"] as const;

/**
 * Formata o tipo de consulta para o template (ex.: "por Teleatendimento").
 */
export function formatTipoConsulta(healthcareDisplay: string): string {
  const value = healthcareDisplay?.trim() || "";
  if (!value) return "";
  if (/^por\s/i.test(value)) return value;

  if (/teleatendimento|^\s*chat\s*$/i.test(value)) {
    return `por ${value}`;
  }

  return value;
}

/**
 * Monta o nome do template: `{cnpj}_cobranca_de_consulta`.
 */
export function buildCobrancaTemplateName(organizationId?: string): string {
  const cnpj = resolveCrmOrganizationId(organizationId);
  if (!cnpj) {
    throw new Error(
      "ID da organização não configurado para template de cobrança.",
    );
  }
  return `${cnpj}${COBRANCA_TEMPLATE_SUFFIX}`;
}

/**
 * Payload padrão de cobrança de consulta (contrato API templates WhatsApp).
 */
export function buildCobrancaConsultaNotification(params: {
  cpf: string;
  nomePaciente: string;
  tipoConsulta: string;
  nomeProfissional: string;
  linkPagamento: string;
  organizationId?: string;
}): INotificationData {
  return {
    to: { cpf: params.cpf },
    channel: [...NOTIFICATION_CHANNEL],
    messageTemplate: buildCobrancaTemplateName(params.organizationId),
    parameters: {
      nome_paciente: params.nomePaciente,
      tipo_consulta: formatTipoConsulta(params.tipoConsulta),
      nome_profissional: params.nomeProfissional,
      link_pagamento: params.linkPagamento,
    },
  };
}

/**
 * Fluxvet: adapta dados do tutor/pet para o mesmo template de cobrança.
 * TODO: confirmar com backend se parâmetros vet exigem template distinto.
 */
export function buildCobrancaConsultaVetNotification(params: {
  cpf: string;
  nomeTutor: string;
  nomePet: string;
  nomeProfissional: string;
  nomeClinica: string;
  linkPagamento: string;
  organizationId?: string;
}): INotificationData {
  const tipoConsulta = [params.nomePet, params.nomeClinica]
    .filter(Boolean)
    .join(" — ");

  return buildCobrancaConsultaNotification({
    cpf: params.cpf,
    nomePaciente: params.nomeTutor,
    tipoConsulta: tipoConsulta || "Consulta veterinária",
    nomeProfissional: params.nomeProfissional,
    linkPagamento: params.linkPagamento,
    organizationId: params.organizationId,
  });
}

/**
 * Resolve o phoneNumberId para a URL de envio.
 * Preferencial: phoneNumberId do Endpoint Meta (contato rank 1).
 * Fallback: número padrão Fluxmed (plano fluxmedDefault).
 */
export async function resolveNotificationPhoneNumberId(params: {
  organizationId?: string;
  phoneNumberId?: string;
}): Promise<string> {
  const explicit = params.phoneNumberId?.trim();
  if (explicit) return explicit;

  const organizationId = params.organizationId?.trim();
  if (organizationId) {
    const config = await endpointService.getWhatsAppEndpointConfigByContext({
      organizationId,
    });
    const fromEndpoint = config.phoneNumberIdRank1?.trim();
    if (fromEndpoint) return fromEndpoint;
  }

  if (DEFAULT_PHONE_NUMBER_ID) return DEFAULT_PHONE_NUMBER_ID;

  throw new Error(
    "Número WhatsApp (phoneNumberId) não configurado para envio de notificação.",
  );
}
